import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

// Casting for Chapter One is closed — the "Apply" nav item is retired and
// replaced by a trailer CTA. The /apply route still resolves (shows the
// Casting Closed page) so stale share links land gracefully.
const nav = [
    { to: "/experiences", label: "Experiences" },
    { to: "/original-series", label: "Original Series" },
    { to: "/community", label: "Community" },
    { to: "/about", label: "About" },
    { to: "/partnerships", label: "Partnerships" },
    { to: "/contact", label: "Contact" },
];

export default function Header() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const loc = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => setOpen(false), [loc.pathname]);

    if (loc.pathname.startsWith("/admin")) return null;

    return (
        <header
            data-testid="site-header"
            className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
                scrolled
                    ? "bg-[color:var(--warm-black)]/85 backdrop-blur-xl border-b border-[color:var(--espresso)]"
                    : "bg-transparent"
            }`}
        >
            <div className="mx-auto flex max-w-[1600px] items-center justify-between px-6 py-5 lg:px-12">
                <Link
                    to="/"
                    data-testid="header-logo"
                    className="flex items-baseline gap-2"
                >
                    <span
                        className="font-serif text-[color:var(--gold)] text-2xl tracking-tight"
                        style={{ fontWeight: 500 }}
                    >
                        Mrs.<span className="italic">Black</span>
                        <span className="text-[color:var(--soft-cream)]">(In)</span>
                        <span className="italic">Love</span>
                    </span>
                </Link>
                <nav className="hidden xl:flex items-center gap-9">
                    {nav.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={({ isActive }) =>
                                `text-[0.72rem] uppercase tracking-[0.24em] link-underline ${
                                    isActive ? "text-[color:var(--gold)]" : ""
                                }`
                            }
                        >
                            {n.label}
                        </NavLink>
                    ))}
                </nav>
                <div className="hidden xl:block">
                    <Link
                        to="/original-series#trailer"
                        data-testid="header-trailer-cta"
                        className="btn-gold btn-gold-solid"
                    >
                        ▶ Watch the trailer
                    </Link>
                </div>
                <button
                    className="xl:hidden text-[color:var(--soft-cream)]"
                    onClick={() => setOpen(!open)}
                    data-testid="header-menu-toggle"
                    aria-label="Toggle menu"
                >
                    {open ? <X strokeWidth={1.25} /> : <Menu strokeWidth={1.25} />}
                </button>
            </div>
            {open && (
                <div
                    data-testid="mobile-menu"
                    className="xl:hidden border-t border-[color:var(--espresso)] bg-[color:var(--warm-black)]"
                >
                    <div className="px-6 py-8 flex flex-col gap-6">
                        {nav.map((n) => (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className="font-serif text-3xl text-[color:var(--soft-cream)]"
                            >
                                {n.label}
                            </NavLink>
                        ))}
                        <Link
                            to="/original-series#trailer"
                            className="btn-gold btn-gold-solid self-start mt-4"
                            data-testid="mobile-trailer-cta"
                        >
                            ▶ Watch the trailer
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
}
